import React from 'react';

import Dialog from '@material-ui/core/Dialog';
import { makeStyles } from '@material-ui/core/styles';
import AssignmentOutlinedIcon from '@material-ui/icons/AssignmentOutlined';
import ChatBubbleOutlineOutlinedIcon from '@material-ui/icons/ChatBubbleOutlineOutlined';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import HomeOutlinedIcon from '@material-ui/icons/HomeOutlined';
import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import PhoneOutlinedIcon from '@material-ui/icons/PhoneOutlined';
import ScheduleOutlinedIcon from '@material-ui/icons/ScheduleOutlined';

const useStyles = makeStyles((theme) => ({
  backdrop: {
    backgroundColor: 'rgba(20, 23, 29, 0.58)',
    backdropFilter: 'blur(4px)',
  },
  paper: {
    width: '100%',
    maxWidth: '720px',
    maxHeight: 'calc(100vh - 40px)',
    margin: '20px',
    overflow: 'hidden',
    border: `1px solid ${theme.palette.grayScale6.main}`,
    borderRadius: '14px',
    background: theme.palette.grayScale8.main,
    boxShadow: '0 28px 80px rgba(20, 23, 29, 0.24)',

    [theme.breakpoints.down('xs')]: {
      maxHeight: 'calc(100vh - 20px)',
      margin: '10px',
      borderRadius: '10px',
    },
  },
  header: {
    position: 'relative',
    padding: '30px 76px 26px 34px',
    borderBottom: `1px solid ${theme.palette.grayScale6.main}`,
    background: theme.palette.white.main,

    [theme.breakpoints.down('xs')]: {
      padding: '24px 62px 22px 22px',
    },
  },
  accent: {
    width: '42px',
    height: '4px',
    marginBottom: '15px',
    borderRadius: '999px',
    background: theme.palette.orange.main,
  },
  eyebrow: {
    margin: '0 0 7px',
    color: theme.palette.orange.main,
    fontSize: '11px',
    fontWeight: 700,
    lineHeight: 1.4,
    letterSpacing: '0.13em !important',
    textTransform: 'uppercase',
  },
  title: {
    margin: 0,
    color: theme.palette.darkGray.main,
    fontSize: '28px',
    fontWeight: 700,
    lineHeight: 1.2,

    [theme.breakpoints.down('xs')]: {
      fontSize: '23px',
    },
  },
  subtitle: {
    margin: '8px 0 0',
    color: theme.palette.lightGray.main,
    fontSize: '13px',
    fontWeight: 400,
    lineHeight: 1.55,
  },
  closeButton: {
    position: 'absolute',
    top: '20px',
    right: '22px',
    width: '38px',
    height: '38px',
    padding: 0,
    border: `1px solid ${theme.palette.grayScale6.main}`,
    borderRadius: '50%',
    background: theme.palette.white.main,
    color: theme.palette.grayScale11.main,
    cursor: 'pointer',
    display: 'grid',
    placeItems: 'center',
    transition: 'border-color 180ms ease, color 180ms ease, background-color 180ms ease',

    '&:hover, &:focus-visible': {
      borderColor: theme.palette.orange.main,
      background: '#fff7f4',
      color: theme.palette.orange.main,
      outline: 'none',
    },
    '&:focus-visible': {
      boxShadow: `0 0 0 3px ${theme.palette.grayScale6.main}`,
    },
    '& svg': {
      width: '20px',
      height: '20px',
    },

    [theme.breakpoints.down('xs')]: {
      top: '14px',
      right: '14px',
      width: '36px',
      height: '36px',
    },
  },
  body: {
    padding: '30px 34px 34px',
    overflowY: 'auto',

    [theme.breakpoints.down('xs')]: {
      padding: '22px',
    },
  },
  contactCard: {
    display: 'grid',
    gridTemplateColumns: '64px minmax(0, 1fr)',
    gap: '18px',
    alignItems: 'center',
    padding: '20px 22px',
    border: `1px solid ${theme.palette.grayScale6.main}`,
    borderLeft: `4px solid ${theme.palette.orange.main}`,
    borderRadius: '8px',
    background: theme.palette.white.main,
    boxShadow: '0 8px 24px rgba(20, 23, 29, 0.05)',

    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '52px minmax(0, 1fr)',
      gap: '14px',
      padding: '18px 16px',
    },
  },
  avatar: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    background: '#fff0eb',
    color: theme.palette.orange.main,
    display: 'grid',
    placeItems: 'center',
    fontSize: '19px',
    fontWeight: 700,
    letterSpacing: '0.04em !important',

    [theme.breakpoints.down('xs')]: {
      width: '52px',
      height: '52px',
      fontSize: '16px',
    },
  },
  submittedBy: {
    margin: '0 0 3px',
    color: theme.palette.lightGray.main,
    fontSize: '10px',
    fontWeight: 700,
    lineHeight: 1.4,
    letterSpacing: '0.11em !important',
    textTransform: 'uppercase',
  },
  name: {
    margin: '0 0 8px',
    color: theme.palette.darkGray.main,
    fontSize: '20px',
    fontWeight: 700,
    lineHeight: 1.3,
    overflowWrap: 'anywhere',
  },
  contactLinks: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '7px 18px',

    '& a': {
      minWidth: 0,
      color: theme.palette.grayScale11.main,
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: 1.45,
      textDecoration: 'none',
      overflowWrap: 'anywhere',
      transition: 'color 180ms ease',
    },
    '& a:hover, & a:focus-visible': {
      color: theme.palette.orange.main,
      textDecoration: 'underline',
      textUnderlineOffset: '3px',
      outline: 'none',
    },
    '& svg': {
      flex: '0 0 auto',
      width: '16px',
      height: '16px',
    },

    [theme.breakpoints.down('xs')]: {
      display: 'grid',
      gap: '6px',
    },
  },
  section: {
    marginTop: '28px',
  },
  sectionTitle: {
    margin: '0 0 12px',
    color: theme.palette.grayScale11.main,
    fontSize: '11px',
    fontWeight: 700,
    lineHeight: 1.4,
    letterSpacing: '0.1em !important',
    textTransform: 'uppercase',
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '12px',

    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  detailCard: {
    minWidth: 0,
    padding: '17px 18px',
    border: `1px solid ${theme.palette.grayScale6.main}`,
    borderRadius: '7px',
    background: theme.palette.white.main,
  },
  detailCardWide: {
    gridColumn: '1 / -1',
  },
  detailLabel: {
    margin: '0 0 7px',
    color: theme.palette.lightGray.main,
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    fontSize: '10px',
    fontWeight: 700,
    lineHeight: 1.4,
    letterSpacing: '0.08em !important',
    textTransform: 'uppercase',

    '& svg': {
      width: '16px',
      height: '16px',
      color: theme.palette.orange.main,
    },
  },
  detailValue: {
    margin: 0,
    color: theme.palette.darkGray.main,
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: 1.55,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  },
  messageCard: {
    padding: '20px',
    border: `1px solid ${theme.palette.grayScale6.main}`,
    borderRadius: '7px',
    background: '#fffaf8',
  },
}));

interface Props {
  title: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  address?: string;
  message?: string;
  requestType?: string;
  sellingTimeline?: string;
  zipCode?: string;
  reportContext?: string;
  onClose: () => void;
}

interface DetailCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  wide?: boolean;
}

const ViewFormSubmissionModal: React.FC<Props> = ({
  title, fullName, email, phoneNumber, address, message, requestType, sellingTimeline, zipCode, reportContext, onClose,
}: Props) => {
  const classes = useStyles();
  const initials = fullName
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((name) => name.charAt(0))
    .join('')
    .toUpperCase() || '?';
  const hasRequestDetails = Boolean(address || requestType || sellingTimeline || zipCode || reportContext);

  const DetailCard = ({
    icon, label, value, wide = false,
  }: DetailCardProps) => (
    <div className={`${classes.detailCard}${wide ? ` ${classes.detailCardWide}` : ''}`}>
      <p className={classes.detailLabel}>
        {icon}
        {label}
      </p>
      <p className={classes.detailValue}>{value}</p>
    </div>
  );

  return (
    <Dialog
      aria-labelledby="lead-response-modal-title"
      BackdropProps={{ className: classes.backdrop }}
      classes={{ paper: classes.paper }}
      fullWidth
      keepMounted
      maxWidth="sm"
      open
      scroll="paper"
      onClose={onClose}
    >
      <header className={classes.header}>
        <button className={classes.closeButton} type="button" aria-label="Close" onClick={onClose}>
          <CloseRoundedIcon aria-hidden="true" />
        </button>
        <div className={classes.accent} aria-hidden="true" />
        <p className={classes.eyebrow}>Lead response</p>
        <h2 className={classes.title} id="lead-response-modal-title">{title}</h2>
        <p className={classes.subtitle}>Contact information and request details submitted from the landing page.</p>
      </header>

      <div className={classes.body}>
        <section className={classes.contactCard} aria-label="Submitted contact information">
          <div className={classes.avatar} aria-hidden="true">{initials}</div>
          <div>
            <p className={classes.submittedBy}>Submitted by</p>
            <h3 className={classes.name}>{fullName}</h3>
            <div className={classes.contactLinks}>
              {phoneNumber && (
                <a href={`tel:${phoneNumber.replace(/[^\d+]/g, '')}`}>
                  <PhoneOutlinedIcon aria-hidden="true" />
                  {phoneNumber}
                </a>
              )}
              {email && (
                <a href={`mailto:${email}`}>
                  <MailOutlineIcon aria-hidden="true" />
                  {email}
                </a>
              )}
            </div>
          </div>
        </section>

        {hasRequestDetails && (
          <section className={classes.section} aria-labelledby="lead-response-details-title">
            <h3 className={classes.sectionTitle} id="lead-response-details-title">Request details</h3>
            <div className={classes.detailGrid}>
              {address && <DetailCard icon={<HomeOutlinedIcon />} label="Property address" value={address} wide />}
              {requestType && <DetailCard icon={<AssignmentOutlinedIcon />} label="Request / interest" value={requestType} />}
              {sellingTimeline && <DetailCard icon={<ScheduleOutlinedIcon />} label="Selling timeline" value={sellingTimeline} />}
              {zipCode && <DetailCard icon={<LocationOnOutlinedIcon />} label="ZIP code" value={zipCode} />}
              {reportContext && <DetailCard icon={<DescriptionOutlinedIcon />} label="Report context" value={reportContext} wide />}
            </div>
          </section>
        )}

        {message && (
          <section className={classes.section} aria-labelledby="lead-response-message-title">
            <h3 className={classes.sectionTitle} id="lead-response-message-title">Message</h3>
            <div className={classes.messageCard}>
              <p className={classes.detailLabel}>
                <ChatBubbleOutlineOutlinedIcon aria-hidden="true" />
                Submitted note
              </p>
              <p className={classes.detailValue}>{message}</p>
            </div>
          </section>
        )}
      </div>
    </Dialog>
  );
};

export default ViewFormSubmissionModal;
